import React, { useState, useMemo, useEffect, useRef, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { PatientDataContext } from '../../../context/PatientDataContext';
import './styles/ReviewDocuments.css';
import { db, auth } from '../../../firebase';
import { TriggerRenderDocTemplate } from '../../../utils/TriggerRenderDocTemplate';
import { InitialWarningDialog } from './components/InitialWarningDialog';
import { doc, runTransaction, serverTimestamp, getDoc } from 'firebase/firestore';
import WarningBar from './components/WarningBar';
import DocumentReviewHeader from './components/DocumentReviewHeader';
import { decodeHtmlEntities } from './functions/htmlEncoders';
import { handlePlainTextPaste, suppressFormattingShortcuts } from './functions/contentEditableUtils';
import { isPatientFemale } from '../../../utils/conditionChecks';
import { rerenderPatientReportIfStale } from '../../../utils/autoPatientReport';
import { rerenderCoverPageIfStale } from '../../../utils/autoCoverPage';
import { useAuth } from '../../../hooks/useAuth';
import { buildFirstApprovedFieldsDotNotation, appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { CheckIcon, PencilIcon, ClipboardListIcon } from '@heroicons/react/solid';
import { CheckCircleIcon } from '@heroicons/react/outline';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { renderQuotedValue } from '../../../utils/renderModelInput';
import {
  emitParagraphApproval,
  emitParagraphEdited,
} from '../../../analytics/reportEvents';

// Detect macOS so keyboard shortcut labels show ⌘ instead of Ctrl.
const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
const modKey = isMac ? '⌘' : 'Ctrl';

// ---------------------------------------------------------------------------
// PL_TO_CI_MAPPING
// ---------------------------------------------------------------------------
// Maps each Patient Report "Your Story" placeholder to the individual CI
// paragraph codes that get concatenated to fill it. This mirrors the backend
// logic in document_functions.py (report_logic_version == 2) where approved
// CI paragraphs are joined with '\n\n' into each pl_* field.
//
// The review page shows each CI paragraph as a separate, independently
// reviewable card, grouped under the Patient Report subsection heading that
// they'll appear in once the report is generated.
//
// Risk Assessment is excluded because it is not part of the Patient Report.
// ---------------------------------------------------------------------------
const PL_TO_CI_MAPPING = {
  pl_presentingComplaintSummary: [
    { code: 'ci_COM_referral_information', title: 'Referral Information' },
    { code: 'ci_COM_history_of_presenting_complaint', title: 'History of Presenting Complaint' },
    { code: 'ci_COM_impact_on_daily_functioning', title: 'Impact on Daily Functioning' },
  ],
  pl_developmentalSocialHistoryChildhoodSummary: [
    { code: 'ci_DEV_pre_natal_and_birth', title: 'Pre-natal and Birth' },
    { code: 'ci_DEV_early_years', title: 'Early Years' },
    { code: 'ci_DEV_childhood_and_adolescence', title: 'Childhood and Adolescence' },
    { code: 'ci_DEV_childhood_living_environment', title: 'Childhood Living Environment' },
    { code: 'ci_DEV_education', title: 'Education' },
  ],
  pl_developmentalSocialHistoryAdultSummary: [
    { code: 'ci_DEV_current_living_situation', title: 'Current Living Situation' },
    { code: 'ci_DEV_relationships', title: 'Relationships' },
    { code: 'ci_DEV_employment', title: 'Employment' },
  ],
  pl_lifestyleHistorySummary: [
    { code: 'ci_LIF_lifestyle_factors', title: 'Lifestyle Factors' },
    { code: 'ci_LIF_alcohol_and_substance_use', title: 'Alcohol and Substance Use' },
    { code: 'ci_LIF_habits', title: 'Habits' },
    { code: 'ci_LIF_driving', title: 'Driving' },
  ],
  pl_forensicHistorySummary: [
    { code: 'ci_FOR_history_of_legal_issues', title: 'History of Legal Issues' },
    { code: 'ci_FOR_perpetrator_history', title: 'History as a Perpetrator' },
    { code: 'ci_FOR_victim_history', title: 'History as a Victim' },
  ],
  pl_familyHistorySummary: [
    { code: 'ci_FAM_health_conditions', title: 'Family Health Conditions' },
  ],
  pl_medicalHistorySummary: [
    { code: 'ci_MED_health_conditions', title: 'Health Conditions' },
    { code: 'ci_MED_hospital_visits', title: 'Hospital Visits' },
    { code: 'ci_MED_medications', title: 'Medications' },
    { code: 'ci_MED_allergies', title: 'Allergies' },
    { code: 'ci_MED_vision_and_hearing', title: 'Vision and Hearing' },
  ],
  pl_psychiatricHistorySummary: [
    { code: 'ci_PSY_previous_and_current_diagnoses', title: 'Previous and Current Diagnoses' },
    { code: 'ci_PSY_previous_and_current_treatments', title: 'Previous and Current Treatments' },
    { code: 'ci_PSY_trauma_and_risk_history', title: 'Trauma and Risk History' },
  ],
  pl_mseSummary: [
    { code: 'ci_MSE_summary', title: 'Mental State Examination' },
  ],
};

// Placeholder keys to skip entirely (Risk Assessment is not included in
// the Patient Report).
const EXCLUDED_PLACEHOLDERS = new Set(['pl_riskAssessmentSummary']);

// ---------------------------------------------------------------------------
// KeyboardShortcutHint – floating action bar at the bottom of the screen.
// Each action is both a keyboard shortcut label and a clickable button,
// replacing the need for separate Approve/Cancel buttons in the AI inputs
// panel during focus mode.
// ---------------------------------------------------------------------------
function KeyboardShortcutHint({
  visible, inFocusMode,
  onApprove, onNext, onPrevious, onReview, onCancel, onReset,
}) {
  const actionClass = 'cursor-pointer rounded-full px-3 py-1 -my-1 hover:bg-white/10 transition-colors';

  return (
    <div
      className={`
        fixed bottom-6 left-1/2 -translate-x-1/2 z-[55]
        bg-gray-900/90 text-white px-4 py-2.5 rounded-full shadow-lg
        text-sm font-medium flex items-center gap-2 backdrop-blur-sm
        transition-all duration-300
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}
      `}
    >
      {inFocusMode ? (
        <>
          <button onClick={onApprove} className={actionClass}>
            <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-xs mr-1">{modKey}+↵</kbd> Approve
          </button>
          <span className="text-gray-600">|</span>
          <button onClick={onCancel} className={actionClass}>
            <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-xs mr-1">Esc</kbd> Cancel
          </button>
          <span className="text-gray-600">|</span>
          <button
            data-reset-btn
            onClick={onReset}
            className={`${actionClass} text-violet-400 opacity-40 cursor-not-allowed`}
          >
            ↺ Reset
          </button>
        </>
      ) : (
        <>
          <button onClick={onApprove} className={actionClass}>
            <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-xs mr-1">{modKey}+↵</kbd> Approve
          </button>
          <span className="text-gray-600">|</span>
          <button onClick={onNext} className={actionClass}>
            <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-xs mr-1">↓</kbd> Next
          </button>
          <span className="text-gray-600">|</span>
          <button onClick={onPrevious} className={actionClass}>
            <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-xs mr-1">↑</kbd> Previous
          </button>
          <span className="text-gray-600">|</span>
          <button onClick={onReview} className={actionClass}>
            <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-xs mr-1">R</kbd> Review
          </button>
        </>
      )}
    </div>
  );
}


// The one Patient Report section this page reviews. RD_CI_v2 reads its section
// from docStructure; here the layout comes from the cached report template, so
// the analytics section is named once, statically.
const PR_REVIEW_SECTION = 'your_story';

// ===========================================================================
// ReviewDocumentPRv2  –  Patient Report "Your Story" Review (Keyboard-First)
// ===========================================================================
// This page lets clinicians review their CI paragraphs in the context of the
// Patient Report template. Instead of showing CI sections (Presenting
// Complaint, Developmental History, etc.), the paragraphs are grouped by
// the Patient Report "Your Story" subsections they'll appear in (Your
// Current Concerns, Your Childhood Experiences, etc.), with template
// boilerplate text displayed faded and uneditable between them.
//
// Key differences from RD_CI_v2.js:
//
// 1. **Template-driven layout** – the section headings and boilerplate text
//    come from the cached Google Doc template (fetched from Firestore at
//    systemConfig/reportTemplateCache). The admin script
//    cache_report_templates.py populates this cache.
//
// 2. **Paragraph grouping** – multiple CI paragraphs are grouped under each
//    Patient Report subsection, with a badge showing how many combine. Each
//    remains independently reviewable.
//
// 3. **Same approval data path** – approvals are written to CI's
//    approvedParagraphs (sections.1) so both this page and RD_CI_v2 are
//    fully data-compatible. The same ci_* codes are used.
//
// All keyboard shortcuts, focus mode, bulk approve, and Firestore logic are
// identical to RD_CI_v2.js.
// ===========================================================================
export default function ReviewDocumentPRv2() {

  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patientDataModel, fetchPatientData, clearPatientData } = useContext(PatientDataContext);
  const { claims } = useAuth();
  const organisationName = claims?.organisation || 'Default';

  // Even though we're showing the Patient Report view, the underlying data
  // is CI paragraphs (module 1). Approvals read from and write to the CI
  // section in Firestore so both review pages stay in sync.
  const MODULE_NUMBER = 1;
  const MODULE_PREFIX_CODE = "ci";
  const MODULE_TITLE = "Patient Report – Your Story";

  const patientIsFemale = useMemo(() => isPatientFemale(patientDataModel), [patientDataModel]);

  useEffect(() => {
    fetchPatientData(patientId);
  }, [patientId, fetchPatientData]);

  // -----------------------------------------------------------------------
  // Build the effective PL-to-CI mapping, conditionally including Women's
  // Health for female patients.
  // -----------------------------------------------------------------------
  const effectivePlToCiMapping = useMemo(() => {
    const mapping = { ...PL_TO_CI_MAPPING };
    if (patientIsFemale) {
      mapping.pl_medicalHistorySummary = [
        ...PL_TO_CI_MAPPING.pl_medicalHistorySummary,
        { code: 'ci_MED_womens_health', title: "Women's Health" },
      ];
    }
    return mapping;
  }, [patientIsFemale]);

  // Flat list of every CI code being reviewed on this page, derived from
  // the mapping. Used for counting, IntersectionObserver, and bulk approve.
  const allReviewableCodes = useMemo(() => {
    return Object.values(effectivePlToCiMapping).flatMap(group => group.map(p => p.code));
  }, [effectivePlToCiMapping]);

  // CI paragraph text keyed by code (e.g. ci_COM_referral_information → "…").
  const REPORT_DATA_OUTPUTS = useMemo(() =>
    patientDataModel?.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.outputs || {},
    [patientDataModel]);

  // The paragraph edit history, read from the SAME already-loaded section as the
  // outputs above. It is what lets the adoption numbers (how much of the AI draft
  // survived) be computed at the moment of an edit rather than by reading the
  // report back out of Firestore later - and because this object is already in
  // memory to render the page, it costs no additional read.
  //
  // In-memory means it can lag a write made elsewhere in the same instant. That
  // only matters for a paragraph's FIRST edit, and only if another session edited
  // it seconds earlier; once the earliest entry exists it stays the earliest
  // forever. The nightly reconciliation reads authoritative Firestore, so a
  // disagreement surfaces there rather than going unnoticed.
  const REPORT_EDIT_HISTORY = useMemo(() =>
    patientDataModel?.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.editHistory || {},
    [patientDataModel]);

  // -----------------------------------------------------------------------
  // Template cache – loaded from Firestore on mount
  // -----------------------------------------------------------------------
  const [templateSections, setTemplateSections] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(true);

  // Named styles from the Google Doc template (HEADING_1, HEADING_2, etc.)
  // containing fontFamily, fontSize, bold, italic, and color. The frontend
  // uses these to render headings and body text with the same typography as
  // the actual report document.
  const [namedStyles, setNamedStyles] = useState({});

  useEffect(() => {
    const loadTemplateCache = async () => {
      try {
        const cacheRef = doc(db, 'systemConfig', 'reportTemplateCache');
        const cacheSnap = await getDoc(cacheRef);
        if (cacheSnap.exists()) {
          const data = cacheSnap.data();
          const cacheKey = `${organisationName}__Patient Report - ADHD`;
          const templateData = data.templates?.[cacheKey];
          if (templateData?.sections) {
            setTemplateSections(templateData.sections);
            if (templateData.namedStyles) {
              setNamedStyles(templateData.namedStyles);
            }
          } else {
            console.warn(`Template cache key "${cacheKey}" not found. Available keys:`,
              Object.keys(data.templates || {}));
          }
        }
      } catch (error) {
        console.error('Error loading template cache:', error);
      } finally {
        setTemplateLoading(false);
      }
    };
    loadTemplateCache();
  }, [organisationName]);

  // Extract the "Your Story" section from the cached template. This gives
  // us the heading order, subsection titles, and boilerplate text that
  // surrounds each placeholder in the actual Google Doc template.
  const yourStorySection = useMemo(() => {
    if (!templateSections) return null;
    return templateSections.find(s =>
      s.heading?.toLowerCase().includes('your story')
    ) || null;
  }, [templateSections]);

  // -----------------------------------------------------------------------
  // Template style helpers
  // -----------------------------------------------------------------------
  // Convert the cached named styles (fontFamily, fontSize in pt, bold,
  // italic, color as hex) into React inline style objects. These are
  // applied to headings and body text so the preview closely matches the
  // actual Google Doc template.
  // -----------------------------------------------------------------------
  const templateStyles = useMemo(() => {
    // Sensible fallbacks that approximate common Google Docs defaults. These
    // ensure the preview still looks reasonable when the template cache
    // doesn't include named style data (e.g. cache hasn't been refreshed
    // after the style-extraction update, or the template is missing styles).
    const FALLBACK_STYLES = {
      HEADING_1: { fontSize: 24, fontWeight: 'bold', color: '#1f2937' },
      HEADING_2: { fontSize: 19, color: '#1f2937' },
      HEADING_3: { fontSize: 14, color: '#063763' },
      NORMAL_TEXT: { fontFamily: '"Arial", sans-serif', fontSize: 11 },
    };

    const toReactStyle = (styleObj, fallbackKey, overrides) => {
      const fallback = fallbackKey ? FALLBACK_STYLES[fallbackKey] : {};
      const merged = { ...fallback, ...(styleObj || {}), ...(overrides || {}) };
      const css = {};

      // fontFamily may already be fully formatted in fallback, or just a
      // bare name from the cache – wrap bare names with a sans-serif stack.
      if (merged.fontFamily) {
        css.fontFamily = merged.fontFamily.includes(',')
          ? merged.fontFamily
          : `"${merged.fontFamily}", sans-serif`;
      }
      if (merged.fontSize) css.fontSize = `${merged.fontSize}pt`;
      if (merged.bold || merged.fontWeight) {
        css.fontWeight = merged.fontWeight || 'bold';
      }
      if (merged.italic) css.fontStyle = 'italic';
      if (merged.color) css.color = merged.color;
      return css;
    };

    // Scan the template's boilerplate blocks for the inline fontSize
    // override that body paragraphs actually use. In practice, all body
    // text in the Google Doc is 12pt even though the NORMAL_TEXT named
    // style is 11pt. We pick up that override so both boilerplate and
    // AI paragraphs render at the same effective size.
    let bodyOverride = null;
    if (yourStorySection) {
      for (const sub of (yourStorySection.subsections || [])) {
        for (const block of (sub.blocks || [])) {
          if (block.type === 'boilerplate' && block.style) {
            bodyOverride = block.style;
            break;
          }
        }
        if (bodyOverride) break;
      }
    }

    const resolvedBody = toReactStyle(namedStyles.NORMAL_TEXT, 'NORMAL_TEXT', bodyOverride);

    return {
      h1: toReactStyle(namedStyles.HEADING_1, 'HEADING_1'),
      h2: toReactStyle(namedStyles.HEADING_2, 'HEADING_2'),
      h3: toReactStyle(namedStyles.HEADING_3, 'HEADING_3'),
      body: resolvedBody,
    };
  }, [namedStyles, yourStorySection]);

  // -----------------------------------------------------------------------
  // State
  // -----------------------------------------------------------------------
  const [selectedCode, setSelectedCode] = useState(null);
  const [focusMode, setFocusMode] = useState(false);
  const [reviewedCodes, setReviewedCodes] = useState(new Set());
  const [totalParagraphs, setTotalParagraphs] = useState(0);
  const [preEditContent, setPreEditContent] = useState({});
  const [modelInputsFirestore, setModelInputsFirestore] = useState(null);
  const [showInitialWarning, setShowInitialWarning] = useState(true);
  const [showWarning, setShowWarning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [sectionDirty, setSectionDirty] = useState(false);

  // Full approval metadata keyed by paragraph code. Each entry mirrors
  // the Firestore approvedParagraphs map: { approvedAt, approvedBy,
  // approvedByName, content }. Used to display "last approved" timestamps.
  const [approvalData, setApprovalData] = useState({});

  // Edit history keyed by paragraph code, then by timestamp key. Used to
  // display "last edited" timestamps. Only the most recent entry per code
  // is shown in the UI.
  const [editHistoryData, setEditHistoryData] = useState({});

  // Controls whether template boilerplate text and section headings are
  // shown (true = full template context) or hidden (false = just the
  // reviewable AI paragraphs). Toggled via a switch in the header.
  const [showTemplateContext, setShowTemplateContext] = useState(true);

  // Shown when the clinician tries to exit focus mode after making edits.
  // Gives them a chance to go back rather than losing their changes.
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // -----------------------------------------------------------------------
  // Refs
  // -----------------------------------------------------------------------
  const documentContainerRef = useRef(null);
  const aiInputsPanelRef = useRef(null);
  // Tracks whether the clinician has made any edits since entering focus
  // mode. Updated imperatively (not via state) to avoid re-renders during
  // typing. The Reset button's disabled state is toggled via direct DOM
  // manipulation using [data-reset-btn].
  const editDirtyRef = useRef(false);

  // -----------------------------------------------------------------------
  // Derived values
  // -----------------------------------------------------------------------
  const activeInputs = useMemo(() => {
    if (!modelInputsFirestore || !selectedCode) return null;
    return modelInputsFirestore[selectedCode] || null;
  }, [modelInputsFirestore, selectedCode]);

  // -----------------------------------------------------------------------
  // Effects – data loading
  // -----------------------------------------------------------------------

  // Count reviewable paragraphs (CI codes that have generated content).
  useEffect(() => {
    const total = allReviewableCodes.filter(
      code => REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== ""
    ).length;
    setTotalParagraphs(total);
  }, [allReviewableCodes, REPORT_DATA_OUTPUTS]);

  // Pull AI model inputs from the patient data model.
  useEffect(() => {
    if (patientDataModel) {
      const inputs = patientDataModel?.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.inputs;
      if (inputs) setModelInputsFirestore(inputs);
    }
  }, [patientDataModel]);

  // Load previously approved CI paragraphs and edit history from Firestore.
  // Stores both the set of approved codes (for progress tracking) and the
  // full approval/edit metadata (for displaying timestamps per paragraph).
  useEffect(() => {
    const loadApprovedParagraphs = async () => {
      try {
        const docRef = doc(db, 'patients', patientId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const sectionData = data.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`] || {};
          const approved = sectionData.approvedParagraphs || {};
          const editHistory = sectionData.editHistory || {};
          setReviewedCodes(new Set(Object.keys(approved)));
          setApprovalData(approved);
          setEditHistoryData(editHistory);
        }
      } catch (error) {
        console.error('Error loading approved paragraphs:', error);
      }
    };
    loadApprovedParagraphs();
  }, [patientId]);

  // Lock body scroll while on this page.
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Toggle warning bar based on unapproved paragraph count.
  useEffect(() => {
    setShowWarning(totalParagraphs > reviewedCodes.size);
  }, [reviewedCodes.size, totalParagraphs]);


  // -----------------------------------------------------------------------
  // Left-border status classes
  // -----------------------------------------------------------------------
  const getStatusBarColor = useCallback((code) => {
    if (reviewedCodes.has(code)) return 'bg-green-500';
    return 'bg-violet-400';
  }, [reviewedCodes]);

  // -----------------------------------------------------------------------
  // Timestamp formatting
  // -----------------------------------------------------------------------
  // Converts a Firestore Timestamp (with .toDate()), a JS Date, or a
  // numeric epoch into a short human-readable string. Shows relative time
  // for recent events ("just now", "5 min ago", "2 hrs ago") and an
  // absolute date for anything older than 24 hours.
  // -----------------------------------------------------------------------
  const formatTimestamp = useCallback((ts) => {
    if (!ts) return null;
    let date;
    if (ts.toDate) date = ts.toDate();
    else if (ts instanceof Date) date = ts;
    else if (typeof ts === 'number') date = new Date(ts);
    else if (typeof ts === 'string') date = new Date(ts);
    else return null;

    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMs / 3600000);

    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin} min ago`;
    if (diffHrs < 24) return `${diffHrs} hr${diffHrs > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }, []);

  // Returns the timestamp of the most recent edit for a given paragraph
  // code by scanning the editHistory map for the highest timestamp key.
  const getLastEditTimestamp = useCallback((code) => {
    const history = editHistoryData[code];
    if (!history) return null;
    const keys = Object.keys(history).sort();
    if (keys.length === 0) return null;
    const latest = history[keys[keys.length - 1]];
    return latest?.timestamp || null;
  }, [editHistoryData]);

  // -----------------------------------------------------------------------
  // Paragraph selection
  // -----------------------------------------------------------------------
  const handleParagraphClick = useCallback((code) => {
    if (focusMode) return;
    setSelectedCode(code);
  }, [focusMode]);

  const handleDocumentClick = useCallback((event) => {
    if (focusMode) return;
    if (!event.target.closest('[data-paragraph-container]')) {
      setSelectedCode(null);
    }
  }, [focusMode]);

  useEffect(() => {
    const container = documentContainerRef.current;
    if (container) container.addEventListener('click', handleDocumentClick);
    return () => {
      if (container) container.removeEventListener('click', handleDocumentClick);
    };
  }, [handleDocumentClick]);

  // -----------------------------------------------------------------------
  // Navigation helpers
  // -----------------------------------------------------------------------
  const selectAndScrollTo = useCallback((code) => {
    setSelectedCode(code);
    setTimeout(() => {
      const el = document.getElementById(code);
      if (el && documentContainerRef.current) {
        const containerRect = documentContainerRef.current.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const scrollTop =
          documentContainerRef.current.scrollTop +
          (elRect.top - containerRect.top) -
          containerRect.height / 3;
        documentContainerRef.current.scrollTo({ top: scrollTop, behavior: 'smooth' });
      }
    }, 50);
  }, []);

  // -----------------------------------------------------------------------
  // Focus mode – enter
  // -----------------------------------------------------------------------
  // Enter focus mode for a paragraph. If a MouseEvent is provided (e.g.
  // from a double-click), the cursor is placed at the click position
  // inside the text. Otherwise the cursor goes to the end of the content.
  const enterFocusMode = useCallback((codeOverride, mouseEvent) => {
    const code = codeOverride || selectedCode;
    if (!code || !modelInputsFirestore) return;

    // Capture the click coordinates before any layout shifts so we can
    // restore the cursor position after the transition completes.
    const clickX = mouseEvent?.clientX;
    const clickY = mouseEvent?.clientY;

    setSelectedCode(code);
    const currentText = REPORT_DATA_OUTPUTS[code] || '';
    setPreEditContent(prev => ({ ...prev, [code]: currentText }));
    editDirtyRef.current = false;
    setFocusMode(true);

    setTimeout(() => {
      const el = document.getElementById(code);
      const container = documentContainerRef.current;
      if (el && container) {
        const wrapper = el.parentElement;
        const target = wrapper || el;
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const scrollTop = container.scrollTop + (targetRect.top - containerRect.top) - 24;
        container.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });

        setTimeout(() => {
          const p = el.querySelector('p');
          if (!p) return;
          p.focus();

          if (clickX != null && clickY != null && document.caretRangeFromPoint) {
            // Place the cursor at the position the user double-clicked.
            // caretRangeFromPoint translates screen coordinates into a
            // text offset within the contentEditable element.
            const caretRange = document.caretRangeFromPoint(clickX, clickY);
            if (caretRange && p.contains(caretRange.startContainer)) {
              const sel = window.getSelection();
              sel.removeAllRanges();
              sel.addRange(caretRange);
              return;
            }
          }

          // Fallback: place cursor at the end of the content.
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(p);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }, 350);
      }
    }, 50);
  }, [selectedCode, modelInputsFirestore, REPORT_DATA_OUTPUTS]);

  // -----------------------------------------------------------------------
  // Focus mode – cancel
  // -----------------------------------------------------------------------
  // Two-step exit: `exitFocusMode` is the public-facing function called by
  // Escape, Cancel button, and overlay double-click. If the clinician has
  // unsaved edits it opens a confirmation dialog instead of exiting
  // immediately. `forceExitFocusMode` performs the actual discard + exit
  // and is called either directly (no edits) or from the dialog's confirm.
  // -----------------------------------------------------------------------
  const forceExitFocusMode = useCallback(() => {
    if (selectedCode && preEditContent[selectedCode] !== undefined) {
      REPORT_DATA_OUTPUTS[selectedCode] = preEditContent[selectedCode];
      setTimeout(() => {
        const el = document.querySelector(`[id="${selectedCode}"] p`);
        if (el) el.textContent = decodeHtmlEntities(preEditContent[selectedCode]);
      }, 0);
    }
    setShowDiscardConfirm(false);
    setFocusMode(false);
  }, [selectedCode, preEditContent, REPORT_DATA_OUTPUTS]);

  const exitFocusMode = useCallback(() => {
    if (selectedCode && preEditContent[selectedCode] !== undefined) {
      const hasUnsavedEdits = REPORT_DATA_OUTPUTS[selectedCode] !== preEditContent[selectedCode];
      if (hasUnsavedEdits) {
        setShowDiscardConfirm(true);
        return;
      }
    }
    forceExitFocusMode();
  }, [selectedCode, preEditContent, REPORT_DATA_OUTPUTS, forceExitFocusMode]);

  // -----------------------------------------------------------------------
  // Firestore – approve a single paragraph
  // -----------------------------------------------------------------------
  const handleParagraphApproval = useCallback(async (code, isApproving) => {
    try {
      await runTransaction(db, async (transaction) => {
        const docRef = doc(db, 'patients', patientId);
        const docSnap = await transaction.get(docRef);
        if (!docSnap.exists()) throw new Error("Document does not exist!");

        const data = docSnap.data();
        const currentApproved =
          data.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.approvedParagraphs || {};

        if (isApproving) {
          currentApproved[code] = {
            approvedAt: serverTimestamp(),
            approvedBy: auth.currentUser.uid,
            approvedByName: auth.currentUser.displayName,
            content: REPORT_DATA_OUTPUTS[code],
          };
        } else {
          delete currentApproved[code];
        }

        transaction.update(docRef, {
          [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.approvedParagraphs`]: currentApproved,
          [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.lastReviewedAt`]: serverTimestamp(),
          [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.reviewedBy`]: auth.currentUser.uid,
          [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.reviewedByName`]: auth.currentUser.displayName,
        });
      });

      // Analytics mirror of the approval change that just persisted
      // (code only). Both directions share the review control that
      // wrote it, so both share this one call.
      emitParagraphApproval({
        patientId,
        module: MODULE_PREFIX_CODE,
        section: PR_REVIEW_SECTION,
        code,
        assessmentType: 'adhd',
        isApproving,
      });

      setSectionDirty(true);
    } catch (error) {
      console.error("Error updating paragraph approval:", error);
    }
  }, [patientId, REPORT_DATA_OUTPUTS]);

  // -----------------------------------------------------------------------
  // Advance to next unreviewed paragraph
  // -----------------------------------------------------------------------
  const advanceToNext = useCallback((currentCode, updatedReviewedCodes) => {
    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const paragraphArray = Array.from(allParagraphs);
    const currentIndex = paragraphArray.findIndex(p => p.id === currentCode);

    const nextUnreviewed = paragraphArray.find((p, index) =>
      index > currentIndex && !updatedReviewedCodes.has(p.id)
    );

    if (nextUnreviewed) {
      selectAndScrollTo(nextUnreviewed.id);
    } else {
      setSelectedCode(null);
    }
  }, [selectAndScrollTo]);

  // -----------------------------------------------------------------------
  // Quick approve (Enter key)
  // -----------------------------------------------------------------------
  const handleQuickApprove = useCallback(() => {
    if (!selectedCode) return;
    const currentCode = selectedCode;

    handleParagraphApproval(currentCode, true).catch(() => {
      setReviewedCodes(prev => {
        const next = new Set(prev);
        next.delete(currentCode);
        return next;
      });
    });

    const newReviewedCodes = new Set([...reviewedCodes, currentCode]);
    setReviewedCodes(newReviewedCodes);
    setApprovalData(prev => ({
      ...prev,
      [currentCode]: {
        ...prev[currentCode],
        approvedAt: new Date(),
        approvedByName: auth.currentUser.displayName,
      },
    }));
    advanceToNext(currentCode, newReviewedCodes);
  }, [selectedCode, reviewedCodes, handleParagraphApproval, advanceToNext]);

  // -----------------------------------------------------------------------
  // Next (Down-arrow key)
  // -----------------------------------------------------------------------
  const handleNext = useCallback(() => {
    if (!selectedCode) return;
    advanceToNext(selectedCode, reviewedCodes);
  }, [selectedCode, reviewedCodes, advanceToNext]);

  // -----------------------------------------------------------------------
  // Previous (Up-arrow key)
  // -----------------------------------------------------------------------
  const handlePrevious = useCallback(() => {
    if (!selectedCode) return;
    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const paragraphArray = Array.from(allParagraphs);
    const currentIndex = paragraphArray.findIndex(p => p.id === selectedCode);
    if (currentIndex > 0) {
      selectAndScrollTo(paragraphArray[currentIndex - 1].id);
    }
  }, [selectedCode, selectAndScrollTo]);

  // -----------------------------------------------------------------------
  // Focus mode approve (Ctrl/Cmd + Enter)
  // -----------------------------------------------------------------------
  const handleFocusModeApprove = useCallback(async () => {
    if (!selectedCode) return;

    const code = selectedCode;
    const currentContent = REPORT_DATA_OUTPUTS[code];
    const originalContent = preEditContent[code];
    const contentChanged = currentContent !== originalContent;

    try {
      if (contentChanged) {
        const editEntry = {
          clinicianId: auth.currentUser.uid,
          clinicianName: auth.currentUser.displayName,
          timestamp: serverTimestamp(),
          previousValue: originalContent,
          newValue: currentContent,
          editType: "manual",
        };
        const timestampKey = Date.now().toString();

        await runTransaction(db, async (transaction) => {
          const docRef = doc(db, 'patients', patientId);
          const docSnap = await transaction.get(docRef);
          if (!docSnap.exists()) throw new Error("Document does not exist!");

          const data = docSnap.data();
          const currentApproved =
            data.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.approvedParagraphs || {};
          const currentEditHistory =
            data.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.editHistory || {};

          if (!currentEditHistory[code]) currentEditHistory[code] = {};
          currentEditHistory[code][timestampKey] = editEntry;

          currentApproved[code] = {
            approvedAt: serverTimestamp(),
            approvedBy: auth.currentUser.uid,
            approvedByName: auth.currentUser.displayName,
            content: currentContent,
          };

          transaction.update(docRef, {
            [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.approvedParagraphs`]: currentApproved,
            [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.editHistory`]: currentEditHistory,
            [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.outputs`]: {
              ...data.dataModel?.['6_manageReports']?.sections?.[MODULE_NUMBER]?.outputs,
              [code]: currentContent,
            },
            [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.lastReviewedAt`]: serverTimestamp(),
            [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.reviewedBy`]: auth.currentUser.uid,
            [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.reviewedByName`]: auth.currentUser.displayName,
          });
        });

        // One transaction, two facts: the rewrite and the approval that came
        // with it. Both are mirrored here (codes and lengths only); the
        // unchanged branch below approves through handleParagraphApproval,
        // which emits its own approval, so neither path emits twice.
        emitParagraphEdited({
          patientId,
          module: MODULE_PREFIX_CODE,
          section: PR_REVIEW_SECTION,
          code,
          prevText: originalContent,
          priorHistory: REPORT_EDIT_HISTORY[code] || {},
          nextText: currentContent,
        });
        emitParagraphApproval({
          patientId,
          module: MODULE_PREFIX_CODE,
          section: PR_REVIEW_SECTION,
          code,
          assessmentType: 'adhd',
          isApproving: true,
        });
      } else {
        await handleParagraphApproval(code, true);
      }

      const now = new Date();
      const newReviewedCodes = new Set([...reviewedCodes, code]);
      setReviewedCodes(newReviewedCodes);
      setApprovalData(prev => ({
        ...prev,
        [code]: {
          ...prev[code],
          approvedAt: now,
          approvedByName: auth.currentUser.displayName,
        },
      }));
      if (contentChanged) {
        setEditHistoryData(prev => ({
          ...prev,
          [code]: {
            ...(prev[code] || {}),
            [Date.now().toString()]: { timestamp: now, clinicianName: auth.currentUser.displayName },
          },
        }));
      }
      setSectionDirty(true);
      setFocusMode(false);
      advanceToNext(code, newReviewedCodes);
    } catch (error) {
      console.error('Focus mode approve failed:', error);
    }
  }, [selectedCode, REPORT_DATA_OUTPUTS, REPORT_EDIT_HISTORY, preEditContent, reviewedCodes, handleParagraphApproval, advanceToNext, patientId]);

  // -----------------------------------------------------------------------
  // Exit review
  // -----------------------------------------------------------------------
  const handleExitReview = useCallback(async () => {
    setIsSaving(true);
    try {
      const isFullyApproved = totalParagraphs > 0 && totalParagraphs === reviewedCodes.size;
      const shouldRenderSection = isFullyApproved && sectionDirty;

      const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
      const updates = {
        [`dataModel.6_manageReports.${MODULE_PREFIX_CODE}ReportApproved`]: isFullyApproved,
        [`dataModel.6_manageReports.${MODULE_PREFIX_CODE}ReportApprovedBy`]: isFullyApproved ? auth.currentUser.uid : null,
        [`dataModel.6_manageReports.${MODULE_PREFIX_CODE}ReportApprovedByName`]: isFullyApproved ? auth.currentUser.displayName : null,
        [`dataModel.6_manageReports.${MODULE_PREFIX_CODE}ReportApprovedAt`]: isFullyApproved ? serverTimestamp() : null,
        ...(isFullyApproved ? buildFirstApprovedFieldsDotNotation(MODULE_PREFIX_CODE, manageReports) : {}),
      };

      await runTransaction(db, async (transaction) => {
        const docRef = doc(db, 'patients', patientId);
        const docSnap = await transaction.get(docRef);
        if (!docSnap.exists()) throw new Error("Document does not exist!");
        transaction.update(docRef, updates);
      });

      appendReportAuditEntry(patientId, {
        module: MODULE_PREFIX_CODE,
        eventType: 'approved',
        isFirstOccurrence: isFullyApproved && !manageReports?.ciReportFirstApprovedAt,
      });

      if (shouldRenderSection) {
        TriggerRenderDocTemplate({
          reportStage: "Clinical Interview",
          clinicianId: auth.currentUser.uid,
          patientId: patientId,
        });
      }

      rerenderPatientReportIfStale({ patientId, clinicianId: auth.currentUser.uid, patientDataModel, organisation: organisationName })
        .catch((e) => console.error('rerenderPatientReportIfStale (PR v2) failed:', e));
      rerenderCoverPageIfStale({ patientId, clinicianId: auth.currentUser.uid, patientDataModel, organisation: organisationName })
        .catch((e) => console.error('rerenderCoverPageIfStale (PR v2) failed:', e));

      clearPatientData();
      setSectionDirty(false);
      navigate(`/patient/${patientId}/documents`);
    } catch (error) {
      console.error('Error during document review completion:', error);
    } finally {
      setIsSaving(false);
    }
  }, [totalParagraphs, reviewedCodes, sectionDirty, patientDataModel, patientId, clearPatientData, navigate, organisationName]);

  // -----------------------------------------------------------------------
  // Reset to pre-edit content (while staying in focus mode)
  // -----------------------------------------------------------------------
  // Reverts the active paragraph back to the text it had when focus mode
  // was entered, letting the clinician start over without exiting and
  // re-entering edit mode.
  // -----------------------------------------------------------------------
  const handleResetToPreEdit = useCallback(() => {
    if (!selectedCode || preEditContent[selectedCode] === undefined) return;
    if (!editDirtyRef.current) return;
    const original = preEditContent[selectedCode];
    REPORT_DATA_OUTPUTS[selectedCode] = original;
    editDirtyRef.current = false;
    setTimeout(() => {
      const el = document.querySelector(`[id="${selectedCode}"] p`);
      if (el) {
        el.textContent = decodeHtmlEntities(original);
        // Place the cursor at the end of the restored text so the
        // clinician can immediately continue typing without clicking.
        el.focus();
        const range = document.createRange();
        const sel = window.getSelection();
        range.selectNodeContents(el);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      // Re-disable the Reset button visually after content is reverted.
      const btn = document.querySelector('[data-reset-btn]');
      if (btn) {
        btn.classList.add('opacity-40', 'cursor-not-allowed');
        btn.classList.remove('hover:bg-violet-900/30');
      }
    }, 0);
  }, [selectedCode, preEditContent, REPORT_DATA_OUTPUTS]);

  const goToFirstUnreviewed = useCallback(() => {
    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const firstUnreviewed = Array.from(allParagraphs).find(p => !reviewedCodes.has(p.id));
    if (firstUnreviewed) selectAndScrollTo(firstUnreviewed.id);
  }, [reviewedCodes, selectAndScrollTo]);

  const calculateProgress = useCallback(() => {
    if (totalParagraphs === 0) return 0;
    return Math.round((reviewedCodes.size / totalParagraphs) * 100);
  }, [reviewedCodes.size, totalParagraphs]);

  // -----------------------------------------------------------------------
  // Keyboard shortcuts
  // -----------------------------------------------------------------------
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showInitialWarning || showDiscardConfirm) return;

      if (focusMode) {
        if (e.key === 'Escape') {
          e.preventDefault();
          exitFocusMode();
        } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          handleFocusModeApprove();
        }
        return;
      }

      // Ctrl/Cmd+Enter works globally (even when no paragraph is selected
      // or focus is in an input) so clinicians can approve from anywhere.
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        if (selectedCode) handleQuickApprove(selectedCode);
        return;
      }

      if (!selectedCode) return;

      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          handleNext();
          break;
        case 'ArrowUp':
          e.preventDefault();
          handlePrevious();
          break;
        case 'r':
        case 'R':
          e.preventDefault();
          enterFocusMode();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    focusMode, selectedCode, showInitialWarning, showDiscardConfirm,
    exitFocusMode, handleFocusModeApprove, handleQuickApprove, handleNext, handlePrevious, enterFocusMode,
  ]);

  // -----------------------------------------------------------------------
  // Scroll lock – prevent the document container from scrolling while in
  // focus mode so the active paragraph stays in view. The lock kicks in
  // after the initial scroll-to-paragraph animation completes (~450ms).
  // Wheel events originating inside the AI inputs panel are allowed
  // through so that panel remains independently scrollable.
  // -----------------------------------------------------------------------
  useEffect(() => {
    const container = documentContainerRef.current;
    if (!container || !focusMode) return;

    const handleWheel = (e) => {
      const aiPanel = container.querySelector('[data-ai-inputs-panel]');
      if (aiPanel && aiPanel.contains(e.target)) {
        // The scrollable region is the inner div (data-ai-inputs-scroll),
        // not the outer panel container which is overflow-hidden.
        const scrollRegion = aiPanel.querySelector('[data-ai-inputs-scroll]') || aiPanel;
        const atTop = scrollRegion.scrollTop <= 0 && e.deltaY < 0;
        const atBottom =
          scrollRegion.scrollTop + scrollRegion.clientHeight >= scrollRegion.scrollHeight - 1 && e.deltaY > 0;
        if (!atTop && !atBottom) return;
      }
      e.preventDefault();
    };

    const lockTimer = setTimeout(() => {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }, 450);

    return () => {
      clearTimeout(lockTimer);
      container.removeEventListener('wheel', handleWheel);
    };
  }, [focusMode]);

  // -----------------------------------------------------------------------
  // AI inputs panel height – dynamically constrain the panel to the space
  // between its top edge and the bottom of the document container, minus a
  // small margin for the floating footer. Recalculates on every frame
  // where the paragraph above changes size (ResizeObserver) and also
  // on every input event for zero-delay response to typing.
  //
  // No CSS transition is used — both the paragraph growth and the panel
  // shrink happen within the same animation frame, keeping them in sync.
  // -----------------------------------------------------------------------
  const panelRafRef = useRef(null);

  const recalcPanelHeight = useCallback(() => {
    // Coalesce multiple calls per frame into one rAF so the browser
    // only performs a single layout/paint per frame.
    if (panelRafRef.current) return;
    panelRafRef.current = requestAnimationFrame(() => {
      panelRafRef.current = null;
      const panel = aiInputsPanelRef.current;
      const container = documentContainerRef.current;
      if (!panel || !container) return;

      const containerRect = container.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const bottomBound = containerRect.bottom - 80;
      const available = bottomBound - panelRect.top;
      panel.style.maxHeight = `${Math.max(120, available)}px`;
    });
  }, []);

  useEffect(() => {
    if (!focusMode) return;

    // Initial calculation after focus-mode animations settle.
    const initTimer = setTimeout(recalcPanelHeight, 500);

    // Watch the active paragraph wrapper for size changes (new lines
    // typed, content reflowing, etc.) and recalculate each time.
    let observer;
    const watchTimer = setTimeout(() => {
      if (!selectedCode) return;
      const paragraphEl = document.getElementById(selectedCode);
      const wrapper = paragraphEl?.closest('[class*="bg-white"]');
      const target = wrapper || paragraphEl;
      if (target) {
        observer = new ResizeObserver(recalcPanelHeight);
        observer.observe(target);
      }
    }, 550);

    return () => {
      clearTimeout(initTimer);
      clearTimeout(watchTimer);
      if (observer) observer.disconnect();
      if (panelRafRef.current) {
        cancelAnimationFrame(panelRafRef.current);
        panelRafRef.current = null;
      }
    };
  }, [focusMode, selectedCode, recalcPanelHeight]);

  // -----------------------------------------------------------------------
  // -----------------------------------------------------------------------
  // renderModelInput – compact AI input renderer
  // -----------------------------------------------------------------------
  // Groups inputs from the same source together to reduce visual
  // repetition. Each role ("Your decisions", "Your notes", etc.) is a
  // labelled section. Within each section, inputs sharing the same source
  // are clustered under a single source tag.
  // -----------------------------------------------------------------------
  const renderModelInput = (role, inputs, Icon) => {
    if (!inputs || Object.keys(inputs).length === 0) {
      return (
        <div className="py-2.5 first:pt-0 last:pb-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1 flex items-center gap-1.5">
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {role}
          </p>
          <p className="text-xs text-gray-400 italic">No inputs used</p>
        </div>
      );
    }

    // Filter to entries that have content, then group by source.
    const entries = Object.entries(inputs).filter(
      ([, input]) => input?.value || input?.source
    );
    if (entries.length === 0) {
      return (
        <div className="py-2.5 first:pt-0 last:pb-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1 flex items-center gap-1.5">
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {role}
          </p>
          <p className="text-xs text-gray-400 italic">No inputs used</p>
        </div>
      );
    }

    // Group entries by their source (e.g. "Clinical Interview > Presenting
    // Complaint") so we display the source label once per group.
    const bySource = {};
    entries.forEach(([key, input]) => {
      const src = input.source || '_ungrouped';
      if (!bySource[src]) bySource[src] = [];
      bySource[src].push({ key, value: input.value || '' });
    });

    return (
      <div className="py-2.5 first:pt-0 last:pb-0">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-800 mb-1.5 flex items-center gap-1.5">
          {Icon && <Icon className="w-3.5 h-3.5" />}
          {role}
        </p>
        {Object.entries(bySource).map(([source, items]) => (
          <div key={source} className="mb-2 last:mb-0">
            {source !== '_ungrouped' && (
              <p className="text-xs font-medium text-gray-600 mb-0.5">In {source}:</p>
            )}
            <ul className="list-disc pl-4">
              {items.map(({ key, value }) => (
                <li key={key} className="text-xs text-gray-800 leading-relaxed whitespace-pre-wrap mb-1 last:mb-0">
                  {renderQuotedValue(value)}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  };

  // -----------------------------------------------------------------------
  // ReviewableParagraph – renders a single CI paragraph card
  // -----------------------------------------------------------------------
  // This is the core rendering unit: one CI paragraph within a Patient
  // Report subsection. Visually similar to the Section component in
  // RD_CI_v2.js but without the section heading (handled by the parent
  // template subsection renderer).
  // -----------------------------------------------------------------------
  const ReviewableParagraph = ({ code, title: subTitle }) => {
    const content = REPORT_DATA_OUTPUTS[code];
    if (content === undefined || content === "") return null;

    const decodedContent = decodeHtmlEntities(content);
    const isActive = selectedCode === code;
    const isApproved = reviewedCodes.has(code);

    // Approval and edit timestamps for the metadata line.
    const approval = approvalData[code];
    const approvedAtStr = formatTimestamp(approval?.approvedAt);
    const lastEditStr = formatTimestamp(getLastEditTimestamp(code));
    const showMeta = approvedAtStr || lastEditStr;

    return (
      <React.Fragment>
        <div
          className={`
            mb-4 transition-all duration-300 relative
            ${focusMode && isActive ? 'bg-white rounded-xl shadow-2xl p-5 z-50' : ''}
            ${focusMode && !isActive ? 'opacity-20 pointer-events-none' : ''}
          `}
        >
          {subTitle && !showTemplateContext && (
            <h4 className="v2-fade-in text-xs font-semibold uppercase tracking-wide mb-1.5 text-gray-400">
              {subTitle}
            </h4>
          )}

          <div
            data-paragraph-container
            id={code}
            className={`
              relative px-4 py-3 -ml-4 rounded-r-lg
              transition-all duration-300
              ${isActive && !focusMode
                ? (isApproved
                    ? 'bg-green-50 ring-2 ring-green-400 ring-inset scale-[1.01]'
                    : 'bg-violet-50/40 ring-2 ring-violet-400 ring-inset scale-[1.01]')
                : ''}
              ${!isActive && !focusMode
                ? (isApproved
                    ? 'bg-green-50/40 hover:bg-green-100/60 hover:scale-[1.01] hover:shadow-md'
                    : 'hover:bg-violet-50/50 hover:scale-[1.01] hover:shadow-md')
                : ''}
              ${!focusMode ? 'cursor-pointer' : ''}
            `}
            onClick={() => !focusMode && handleParagraphClick(code)}
            onDoubleClick={(e) => {
              if (focusMode) return;
              e.preventDefault();
              enterFocusMode(code, e);
            }}
          >
            {/* Status indicator bar – hidden when the active paragraph is
                in focus/edit mode for a cleaner editing experience. */}
            {!(focusMode && isActive) && (
              <div
                className={`absolute top-0 bottom-0 rounded-full transition-all duration-300
                  ${getStatusBarColor(code)}
                  ${isActive ? 'w-1.5 -left-1' : 'w-1 left-0'}
                  ${isActive && !focusMode ? 'v2-paragraph-active' : ''}`}
              />
            )}
            <p
              className="text-gray-700 leading-relaxed whitespace-pre-wrap pr-8 outline-none"
              style={templateStyles.body}
              contentEditable={focusMode && isActive}
              onKeyDown={(e) => {
                suppressFormattingShortcuts(e);
                // Stop scroll-triggering keys from bubbling to the
                // scrollable container. The character is still inserted
                // in the contentEditable (only propagation is stopped,
                // not the default action).
                if (focusMode && isActive && [' ', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key)) {
                  e.stopPropagation();
                }
              }}
              onInput={(e) => {
                if (focusMode && isActive) {
                  const newContent = e.target.innerText;
                  if (REPORT_DATA_OUTPUTS) REPORT_DATA_OUTPUTS[code] = newContent;
                  recalcPanelHeight();
                  if (!editDirtyRef.current) {
                    editDirtyRef.current = true;
                    const btn = document.querySelector('[data-reset-btn]');
                    if (btn) {
                      btn.classList.remove('opacity-40', 'cursor-not-allowed');
                      btn.classList.add('hover:bg-violet-900/30');
                    }
                  }
                }
              }}
              onPaste={handlePlainTextPaste}
              suppressContentEditableWarning={true}
            >
              {decodedContent}
            </p>

            {/* Approval / edit metadata – small italic line below the text.
                Shown in both normal and focus mode so the clinician always
                knows the review history of the paragraph at a glance. */}
            {showMeta && (
              <p className="mt-1.5 text-xs italic text-gray-400 select-none">
                {approvedAtStr && (
                  <span>
                    <CheckIcon className="inline w-3 h-3 mr-0.5 -mt-px text-green-500" />
                    Approved {approvedAtStr}
                    {approval?.approvedByName ? ` by ${approval.approvedByName}` : ''}
                  </span>
                )}
                {approvedAtStr && lastEditStr && <span className="mx-1.5">·</span>}
                {lastEditStr && (
                  <span>Edited {lastEditStr}</span>
                )}
              </p>
            )}

            
          </div>
        </div>

        {/* Inline AI inputs panel in focus mode – constrained to remaining
            viewport height with its own scrollbar so the clinician can
            browse inputs while the parent scroll is locked. */}
        {focusMode && isActive && activeInputs && (
          <div
            ref={aiInputsPanelRef}
            data-ai-inputs-panel
            className="relative z-50 bg-gray-50 rounded-lg shadow-xl mb-6 v2-ai-inputs-slide-in overflow-hidden flex flex-col"
          >
            <p className="shrink-0 text-sm font-bold uppercase tracking-wider text-white px-5 pt-4 pb-3 bg-primary rounded-t-lg">AI inputs used to write this draft:</p>
            <div data-ai-inputs-scroll className="overflow-y-auto px-5 py-4">
            <div className="divide-y divide-primary/10">
              {renderModelInput("Your decisions", activeInputs["Clinician Decisions"], CheckCircleIcon)}
              {renderModelInput("Your notes", activeInputs["Clinician Notes"], PencilIcon)}
              {renderModelInput("Patient's self-report", activeInputs["Patient Prefill"], ClipboardListIcon)}
            </div>
            </div>
          </div>
        )}
      </React.Fragment>
    );
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <Layout activePage="dashboard" scrollable={false}>
      {showInitialWarning && (
        <InitialWarningDialog
          showInitialWarning={showInitialWarning}
          setShowInitialWarning={setShowInitialWarning}
        />
      )}

      <div className="h-full flex flex-col overflow-hidden relative">
        {/* Header */}
        <div className="flex-none pt-4">
          <DocumentReviewHeader
            title={`Reviewing: ${MODULE_TITLE}`}
            reviewedCodes={reviewedCodes}
            totalParagraphs={totalParagraphs}
            isSaving={isSaving}
            handleExitReview={handleExitReview}
            goToFirstUnreviewed={goToFirstUnreviewed}
            calculateProgress={calculateProgress}
            showTemplateContext={showTemplateContext}
            onToggleTemplateContext={() => setShowTemplateContext(prev => !prev)}
          />
          <WarningBar show={showWarning} />
        </div>

        {/* Scrollable document area */}
        <div className={`flex-1 px-8 pb-8 min-h-0 relative ${focusMode ? 'z-50' : ''}`}>
          <div className="document-display-container v2-full-height h-full overflow-hidden bg-slate-800 relative">
            <div
              ref={documentContainerRef}
              className={`
                mx-auto max-w-4xl h-full overflow-y-auto p-16 mt-4 relative
                transition-all duration-300
                ${focusMode
                  ? 'bg-gray-100 border-gray-200 shadow-inner'
                  : 'bg-white shadow-md'
                }
              `}
            >
              {/* Focus-mode overlay – lives inside the scrollable container
                  so it shares the same stacking context as the paragraph
                  wrappers and AI inputs panel (z-50). This ensures those
                  elements render above the overlay (z-40). */}
              <div
                className={`fixed inset-0 bg-black/30 backdrop-blur-sm transition-all duration-300
                  ${focusMode ? 'opacity-100 z-40 cursor-pointer' : 'opacity-0 backdrop-blur-none -z-10 pointer-events-none'}`}
                onDoubleClick={focusMode ? exitFocusMode : undefined}
              />
              {/* Loading state while template cache is being fetched */}
              {templateLoading && (
                <div className="flex items-center justify-center py-20">
                  <div className="text-gray-400 text-lg">Loading template structure...</div>
                </div>
              )}

              {/* Template not found fallback */}
              {!templateLoading && !yourStorySection && (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <p className="text-gray-500 text-lg mb-2">Template structure not found</p>
                    <p className="text-gray-400 text-sm">
                      Run the admin script to cache the template for "{organisationName}".
                    </p>
                  </div>
                </div>
              )}

              {/* ──── "Your Story" section from the template ──── */}
              {!templateLoading && yourStorySection && (
                <div className="space-y-8 text-left pl-6">
                  {/* Main section heading from the template, styled to
                      match the Google Doc's HEADING_1 definition. Hidden
                      smoothly when the template context toggle is off. */}
                  {showTemplateContext && (
                    <h1
                      className={`v2-fade-in transition-opacity duration-300
                        ${focusMode ? 'opacity-20' : ''}`}
                      style={templateStyles.h1}
                    >
                      {yourStorySection.heading}
                    </h1>
                  )}

                  {/* Section-level intro boilerplate (sits above all subsections).
                      Uses the resolved body text style from the template. */}
                  {showTemplateContext && yourStorySection.blocks?.map((block, i) => (
                    block.type === 'boilerplate' && (
                      <p
                        key={`intro-${i}`}
                        className={`v2-fade-in leading-relaxed text-gray-500
                          transition-opacity duration-300 ${focusMode ? 'opacity-10' : ''}`}
                        style={templateStyles.body}
                      >
                        {block.text}
                      </p>
                    )
                  ))}

                  {/* Iterate through each "Your Story" subsection from the
                      cached template (e.g. "Your Current Concerns"). For each
                      subsection, we render its boilerplate text and then the
                      individual CI paragraphs that map to its placeholder. */}
                  {yourStorySection.subsections?.map((subsection, subIdx) => {
                    // Find the placeholder key within this subsection's blocks
                    const placeholderBlock = subsection.blocks?.find(b => b.type === 'placeholder');
                    const placeholderKey = placeholderBlock?.key;

                    // Skip excluded placeholders (e.g. Risk Assessment)
                    if (placeholderKey && EXCLUDED_PLACEHOLDERS.has(placeholderKey)) return null;
                    
                    return (
                      <div key={subIdx} className="mb-8">
                        {/* Subsection heading – styled to match the Google
                            Doc template when context is on, or shown as a
                            plain section heading (like RD_CI_v2) when off */}
                        {showTemplateContext ? (
                          <h2
                            className={`v2-fade-in mb-3 transition-opacity duration-300
                              ${focusMode ? 'opacity-20' : ''}`}
                            style={templateStyles.h2}
                          >
                            {subsection.heading}
                          </h2>
                        ) : (
                          <h2
                            className={`v2-fade-in text-xl font-bold mb-4 text-gray-800 transition-opacity duration-300
                              ${focusMode ? 'opacity-20' : ''}`}
                          >
                            {subsection.heading}
                          </h2>
                        )}

                        {/* Render blocks in order: boilerplate text, then
                            CI paragraphs where the placeholder appears */}
                        {subsection.blocks?.map((block, blockIdx) => {
                          if (block.type === 'boilerplate') {
                            if (!showTemplateContext) return null;
                            return (
                              <p
                                key={`bp-${blockIdx}`}
                                className={`v2-fade-in leading-relaxed mb-4 text-gray-500
                                  transition-opacity duration-300 ${focusMode ? 'opacity-10' : ''}`}
                                style={templateStyles.body}
                              >
                                {block.text}
                              </p>
                            );
                          }

                          if (block.type === 'placeholder' && !EXCLUDED_PLACEHOLDERS.has(block.key)) {
                            const paragraphs = effectivePlToCiMapping[block.key] || [];
                            const availableParagraphs = paragraphs.filter(
                              p => REPORT_DATA_OUTPUTS[p.code] !== undefined && REPORT_DATA_OUTPUTS[p.code] !== ""
                            );

                            if (availableParagraphs.length === 0) return null;

                            return (
                              <div key={`ph-${blockIdx}`}>
                                {availableParagraphs.map(({ code, title }) => (
                                  <ReviewableParagraph key={code} code={code} title={title} />
                                ))}
                              </div>
                            );
                          }

                          return null;
                        })}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Extra bottom padding for focus mode scroll */}
              <div className="h-[calc(100vh-32rem)]" aria-hidden="true" />
            </div>
          </div>
        </div>

        {/* Floating action bar – keyboard shortcuts + clickable buttons */}
        <KeyboardShortcutHint
          visible={Boolean(selectedCode)}
          inFocusMode={focusMode}
          onApprove={focusMode ? handleFocusModeApprove : handleQuickApprove}
          onNext={handleNext}
          onPrevious={handlePrevious}
          onReview={() => enterFocusMode()}
          onCancel={exitFocusMode}
          onReset={handleResetToPreEdit}
        />

        {/* Discard edits confirmation */}
        <AlertDialog.Root open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
          <AlertDialog.Portal>
            <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]" />
            <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] bg-white rounded-lg shadow-xl p-6 max-w-sm w-[90vw] focus:outline-none">
              <AlertDialog.Title className="text-base font-semibold text-gray-900 mb-2">
                Discard changes?
              </AlertDialog.Title>
              <AlertDialog.Description className="text-sm text-gray-500 mb-5">
                Your edits to this paragraph have not been saved and will be lost.
              </AlertDialog.Description>
              <div className="flex justify-end gap-3">
                <AlertDialog.Cancel asChild>
                  <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500">
                    Keep editing
                  </button>
                </AlertDialog.Cancel>
                <AlertDialog.Action asChild>
                  <button
                    onClick={forceExitFocusMode}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Discard
                  </button>
                </AlertDialog.Action>
              </div>
            </AlertDialog.Content>
          </AlertDialog.Portal>
        </AlertDialog.Root>
      </div>
    </Layout>
  );
}
